import { Input, OnInit, Component } from '@angular/core';
import { FormGroup, Validators, FormBuilder, AbstractControl } from '@angular/forms';

import {
  of,
  tap,
  finalize,
  catchError,
  Observable,
  BehaviorSubject,
  Subscription,
  debounceTime,
} from 'rxjs';

import { COUNTRIES } from '@shared/core';
import { CreateUpdateLocationRequest, Location } from '@shared/location/models';
import { FORBIDDEN_NAMES } from '@shared/restaurant/constants';
import { Account } from '@shared/account/models';
import { Country } from '@shared/types';
import {
  ValidationService,
  forbiddenNamesValidator,
  ACCOUNT_LOCATION_NAME_PATTERN,
  urlValidator,
  TelService,
} from '@tablesready/common-ui';
import { LocationApiService } from '../../services';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { Address, Coords } from '@tablesready/frontend-google-places';

const FORM_VALIDATE_TIME = 1500;

@Component({
  selector: 'tablesready-edit-location',
  templateUrl: './add-edit-location.component.html',
})
export class AddEditLocationComponent implements OnInit {
  @Input() location: Location = null;
  @Input() account?: Account;

  title: string;
  form: FormGroup;
  icon = AppIcon;
  timezones: string[];
  isEditMode = false;
  countries = COUNTRIES;
  userCountry$ = new BehaviorSubject<string>(null);
  submitting = false;

  private readonly subscriptions = new Subscription();
  private googleIntegrationEnabled = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly modalController: FrontendModalService,
    private readonly alertsService: AlertsService,
    private readonly validationService: ValidationService,
    private readonly locationApiService: LocationApiService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly telephoneService: TelService,
  ) {}

  ngOnInit(): void {
    this.title = this.location ? 'Edit Location' : 'Add Location';
    this.isEditMode = !!this.location;
    const countryCode = this.location?.country || 'US';
    const country = COUNTRIES.find(item => item.abbr === countryCode);
    this.timezones = country.zones;
    this.userCountry$.next(country.abbr);
    this.initForm(country);

    if (this.isEditMode) {
      this.validationService.validateField(this.form.get('primaryAddress'));
    }
  }

  submit(): void {
    this.validationService.validateForm(this.form);
    if (!this.form.valid || this.submitting) {
      return;
    }

    const value = this.form.value;
    const data: CreateUpdateLocationRequest = {
      name: value.name,
      publicUrl: value.publicUrl,
      websiteUrl: value.websiteUrl,
      phoneNumber: value.phoneNumber,
      timezone: value.timezone,
      country: value.country,
      googleIntegrationEnabled: this.googleIntegrationEnabled,
      address: {
        primaryAddress: value.primaryAddress,
        additionalAddress: value.additionalAddress,
        postalCode: value.postalCode,
        city: value.city,
        state: value.state,
        lat: value.lat,
        lng: value.lng,
      },
    };

    const methodToExecute: Observable<void | Location> = this.location
      ? this.locationApiService.updateById(this.location._id, data)
      : this.locationApiService.create(data);

    this.submitting = true;
    methodToExecute
      .pipe(
        tap(() => {
          let message = 'Location added successfully!';
          let title = 'Created: ';
          if (this.location?._id) {
            message = 'Location edited successfully!';
            title = 'Updated: ';
          }
          this.alertsService.addSuccess(message, title);

          this.close();
        }),
        catchError(err => {
          this.alertsService.addError(err.error.message || err.error);

          return of(null);
        }),
        finalize(() => this.submitting = false),
      ).subscribe();
  }

  changeCountry(): void {
    const country = COUNTRIES.find(country => country.abbr === this.form.get('country').value);
    const phoneNumber = this.telephoneService.format(this.location?.phoneNumber, country.abbr);
    this.timezones = country.zones;
    this.timezoneControl.setValue(this.timezones[0]);
    this.phoneNumberControl.setValue(phoneNumber);
    this.userCountry$.next(country.abbr);
  }

  applyCoords(coords: Coords): void {
    this.form.patchValue(coords);
  }

  applyAddress(address: Address): void {
    this.form.patchValue(address);
  }

  close(): void {
    this.modalController.dismiss();
  }

  get locationNameControl(): AbstractControl {
    return this.form.get('name');
  }

  get publicUrlControl(): AbstractControl {
    return this.form.get('publicUrl');
  }

  get websiteUrlControl(): AbstractControl {
    return this.form.get('websiteUrl');
  }

  get phoneNumberControl(): AbstractControl {
    return this.form.get('phoneNumber');
  }

  get countryControl(): AbstractControl {
    return this.form.get('country');
  }

  get timezoneControl(): AbstractControl {
    return this.form.get('timezone');
  }

  get cityControl(): AbstractControl {
    return this.form.get('city');
  }

  get stateControl(): AbstractControl {
    return this.form.get('state');
  }

  get postalCodeControl(): AbstractControl {
    return this.form.get('postalCode');
  }

  private initForm(country: Country): void {
    this.form = this.fb.group({
      name: [
        this.location?.name || '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(ACCOUNT_LOCATION_NAME_PATTERN),
          forbiddenNamesValidator(FORBIDDEN_NAMES),
        ],
      ],
      publicUrl: [
        this.location?.publicUrl || '',
        [
          Validators.required,
          Validators.maxLength(12),
          Validators.pattern(/^[a-z0-9-_]+$/),
        ],
      ],
      websiteUrl: [
        this.account?.websiteUrl || this.location?.websiteUrl || '',
        [urlValidator],
      ],
      phoneNumber: [
        this.location?.phoneNumber || '',
        [
          Validators.required,
          this.validationService.phonePatternCountryValidator(() => country.abbr),
        ],
      ],
      country: [country.abbr, [Validators.required]],
      timezone: [this.location?.timezone || this.timezones[0], [Validators.required]],
      primaryAddress: [this.location?.address?.primaryAddress || '', [this.validationService.validateGoogleAddress, Validators.required]],
      additionalAddress: [this.location?.address?.additionalAddress || '', [this.validationService.lettersNumbersHyphenQuotesSpacesCommas]],
      city: [this.location?.address?.city || '', [Validators.required]],
      state: [this.location?.address?.state || '', [Validators.required]],
      postalCode: [this.location?.address?.postalCode || '', [Validators.required]],
      lat: [this.location?.address?.lat || undefined],
      lng: [this.location?.address?.lng || undefined],
    });

    const formChangesSub = this.form.valueChanges
      .pipe(debounceTime(FORM_VALIDATE_TIME))
      .subscribe(() => {
        this.form.markAsTouched();
      });

    const authUserSub = this.authRestaurantService.selectAndDetectGoogleIntegrationAvailability()
      .pipe(
        tap(user => {
          this.googleIntegrationEnabled = user.location?.integrations?.reserveWithGoogle?.enabled || false;

          this.googleIntegrationEnabled
            ? this.websiteUrlControl.addValidators(Validators.required)
            : this.websiteUrlControl.removeValidators(Validators.required);

          this.websiteUrlControl.updateValueAndValidity();
        }),
      ).subscribe();

    this.subscriptions.add(formChangesSub);
    this.subscriptions.add(authUserSub);
  }
}
